// RecordingManager.js - Gestione registrazione completa della sessione

class RecordingManager {
  constructor() {
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.canvas = null;
    this.canvasContext = null;
    this.animationFrame = null;
    this.audioContext = null;
    this.audioDestination = null;
    this.audioSources = [];
    this.isRecording = false;
    this.startTime = null;
    this.participants = [];
  }

  /**
   * Inizia la registrazione di tutti i partecipanti
   * @param {Array} participants - Array di partecipanti con loro stream
   * @param {Object} options - Opzioni di registrazione
   */
  async startRecording(participants, options = {}) {
    if (this.isRecording) {
      console.warn('Registrazione già in corso');
      return false;
    }

    try {
      this.participants = participants.filter(p => p.stream);
      
      if (this.participants.length === 0) {
        throw new Error('Nessun partecipante da registrare');
      }

      // Configurazione canvas per video composito
      const width = options.width || 1920;
      const height = options.height || 1080;
      
      this.canvas = document.createElement('canvas');
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvasContext = this.canvas.getContext('2d');

      // Setup audio mixer
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.audioDestination = this.audioContext.createMediaStreamDestination();
      this.audioSources = [];

      // Aggiungi tutti gli stream audio
      this.participants.forEach(participant => {
        if (participant.stream) {
          const audioTracks = participant.stream.getAudioTracks();
          if (audioTracks.length > 0) {
            const source = this.audioContext.createMediaStreamSource(
              new MediaStream(audioTracks)
            );
            source.connect(this.audioDestination);
            this.audioSources.push(source);
          }
        }
      });

      // Combina canvas video stream e audio mixer
      const canvasStream = this.canvas.captureStream(30); // 30 FPS
      const compositeStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...this.audioDestination.stream.getAudioTracks()
      ]);

      // Crea MediaRecorder
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(compositeStream, {
        mimeType: mimeType,
        videoBitsPerSecond: options.videoBitsPerSecond || 2500000, // 2.5 Mbps
        audioBitsPerSecond: options.audioBitsPerSecond || 128000    // 128 kbps
      });

      this.recordedChunks = [];
      this.startTime = Date.now();

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.saveRecording();
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('Errore MediaRecorder:', event.error);
        this.stopRecording();
      };

      // Inizia registrazione
      this.mediaRecorder.start(1000); // Salva chunk ogni secondo
      this.isRecording = true;

      // Inizia rendering canvas
      this.renderCanvas();

      console.log('✅ Registrazione avviata:', {
        participants: this.participants.length,
        mimeType: mimeType,
        resolution: `${width}x${height}`
      });

      return true;
    } catch (error) {
      console.error('❌ Errore avvio registrazione:', error);
      this.cleanup();
      return false;
    }
  }

  /**
   * Ferma la registrazione e salva il file
   */
  stopRecording() {
    if (!this.isRecording) {
      console.warn('Nessuna registrazione in corso');
      return;
    }

    this.isRecording = false;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    console.log('⏹️ Registrazione fermata');
  }

  /**
   * Renderizza tutti i partecipanti sul canvas
   */
  renderCanvas() {
    if (!this.isRecording || !this.canvasContext) return;

    const ctx = this.canvasContext;
    const canvas = this.canvas;
    
    // Pulisci canvas
    ctx.fillStyle = '#0A0E27';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calcola layout griglia
    const participantCount = this.participants.length;
    const { cols, rows } = this.calculateGridLayout(participantCount);
    const cellWidth = canvas.width / cols;
    const cellHeight = canvas.height / rows;

    // Disegna ogni partecipante
    this.participants.forEach((participant, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = col * cellWidth;
      const y = row * cellHeight;

      // Crea video element se non esiste
      if (!participant.videoElement) {
        participant.videoElement = document.createElement('video');
        participant.videoElement.srcObject = participant.stream;
        participant.videoElement.muted = true;
        participant.videoElement.play().catch(e => console.error('Errore play video:', e));
      }

      const video = participant.videoElement;

      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        // Calcola aspect ratio per centrare il video
        const videoAspect = video.videoWidth / video.videoHeight;
        const cellAspect = cellWidth / cellHeight;
        
        let drawWidth, drawHeight, drawX, drawY;
        
        if (videoAspect > cellAspect) {
          // Video più largo
          drawHeight = cellHeight;
          drawWidth = cellHeight * videoAspect;
          drawX = x + (cellWidth - drawWidth) / 2;
          drawY = y;
        } else {
          // Video più alto
          drawWidth = cellWidth;
          drawHeight = cellWidth / videoAspect;
          drawX = x;
          drawY = y + (cellHeight - drawHeight) / 2;
        }

        // Disegna video
        ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);

        // Aggiungi bordo
        ctx.strokeStyle = 'rgba(255, 107, 53, 0.5)';
        ctx.lineWidth = 3;
        ctx.strokeRect(x + 2, y + 2, cellWidth - 4, cellHeight - 4);

        // Aggiungi nome partecipante
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(x + 10, y + cellHeight - 50, cellWidth - 20, 40);
        
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          participant.displayName || 'Partecipante',
          x + cellWidth / 2,
          y + cellHeight - 20
        );

        // Indicatore audio (se microfono attivo)
        if (!participant.audioMuted) {
          ctx.fillStyle = '#10b981';
          ctx.beginPath();
          ctx.arc(x + 30, y + cellHeight - 30, 8, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        // Placeholder se video non pronto
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(x, y, cellWidth, cellHeight);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Caricamento...', x + cellWidth / 2, y + cellHeight / 2);
      }
    });

    // Aggiungi timestamp
    const duration = Math.floor((Date.now() - this.startTime) / 1000);
    const timeString = this.formatDuration(duration);
    
    ctx.fillStyle = 'rgba(239, 68, 68, 0.9)';
    ctx.fillRect(canvas.width - 200, 20, 180, 50);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`⏺ REC ${timeString}`, canvas.width - 190, 50);

    // Continua rendering
    this.animationFrame = requestAnimationFrame(() => this.renderCanvas());
  }

  /**
   * Calcola il layout della griglia in base al numero di partecipanti
   */
  calculateGridLayout(count) {
    if (count === 1) return { cols: 1, rows: 1 };
    if (count === 2) return { cols: 2, rows: 1 };
    if (count <= 4) return { cols: 2, rows: 2 };
    if (count <= 6) return { cols: 3, rows: 2 };
    if (count <= 9) return { cols: 3, rows: 3 };
    if (count <= 12) return { cols: 4, rows: 3 };
    if (count <= 16) return { cols: 4, rows: 4 };
    return { cols: 5, rows: Math.ceil(count / 5) };
  }

  /**
   * Formatta durata in formato MM:SS
   */
  formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Salva la registrazione come file
   */
  saveRecording() {
    if (this.recordedChunks.length === 0) {
      console.error('Nessun dato registrato');
      return;
    }

    const mimeType = this.getSupportedMimeType();
    const blob = new Blob(this.recordedChunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
    const filename = `LuminAI-Recording-${timestamp}.${extension}`;

    // Crea link per download
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);

    const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
    console.log(`💾 Registrazione salvata: ${filename} (${sizeMB} MB)`);

    this.cleanup();
  }

  /**
   * Ottieni il MIME type supportato dal browser
   */
  getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm',
      'video/mp4'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm'; // Fallback
  }

  /**
   * Aggiorna i partecipanti durante la registrazione
   */
  updateParticipants(participants) {
    if (!this.isRecording) return;
    
    this.participants = participants.filter(p => p.stream);
    
    // Rimuovi vecchie sorgenti audio
    this.audioSources.forEach(source => {
      try {
        source.disconnect();
      } catch (e) {}
    });
    this.audioSources = [];

    // Aggiungi nuove sorgenti audio
    this.participants.forEach(participant => {
      if (participant.stream) {
        const audioTracks = participant.stream.getAudioTracks();
        if (audioTracks.length > 0) {
          try {
            const source = this.audioContext.createMediaStreamSource(
              new MediaStream(audioTracks)
            );
            source.connect(this.audioDestination);
            this.audioSources.push(source);
          } catch (e) {
            console.error('Errore aggiunta sorgente audio:', e);
          }
        }
      }
    });
  }

  /**
   * Pulisci risorse
   */
  cleanup() {
    this.isRecording = false;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.audioSources) {
      this.audioSources.forEach(source => {
        try {
          source.disconnect();
        } catch (e) {}
      });
      this.audioSources = [];
    }

    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch (e) {}
      this.audioContext = null;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder = null;
    }

    // Pulisci video elements
    if (this.participants) {
      this.participants.forEach(p => {
        if (p.videoElement) {
          p.videoElement.srcObject = null;
          p.videoElement = null;
        }
      });
    }

    this.canvas = null;
    this.canvasContext = null;
    this.participants = [];
    this.recordedChunks = [];
  }

  /**
   * Verifica se la registrazione è in corso
   */
  getIsRecording() {
    return this.isRecording;
  }

  /**
   * Ottieni durata registrazione corrente
   */
  getRecordingDuration() {
    if (!this.isRecording || !this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }
}

// Esporta singleton
const recordingManager = new RecordingManager();
export default recordingManager;
